/* eslint-disable @typescript-eslint/naming-convention */

// ---------------- CATEGORIES: ---------------
export interface ICategoryApiModel {
  category_images?: { [key: string]: { id: number; url: string } } | [];
  id: number;
  orderBy: number;
  name: string;
  url: string;
  productCount: number;
  description: string;
  enabled: boolean;
  isSampleCategory: boolean;
  imageUrl?: string;
  parentId?: number;
}

export interface IUpsertCategoryPayload {
  id?: number;
  parentId?: any,
  name: string;
  description?: string;
  category_images?: number;
  active?: boolean;
  image?: string;
}
// ---------------- ORDERS: ---------------
export interface IOrderApiModel {
  id: string;
  refundedAmount: number;
  subtotal: number;
  subtotalWithoutTax: number;
  total: number;
  totalWithoutTax: number;
  giftCardRedemption: number;
  totalBeforeGiftCardRedemption: number;
  giftCardDoubleSpending: boolean;
  email: string;
  tax: number;
  b2b_b2c: string;
  ipAddress: string;
  couponDiscount: number;
  paymentStatus: string;
  fulfillmentStatus: string;
  orderNumber: number;
  vendorOrderNumber: string;
  refererUrl: string;
  volumeDiscount: number;
  customerId: number;
  membershipBasedDiscount: number;
  totalAndMembershipBasedDiscount: number;
  customSurcharges: any[];
  discount: number;
  usdTotal: number;
  globalReferer: string;
  createDate: string;
  updateDate: string;
  createTimestamp: number;
  updateTimestamp: number;
  items: ProductOrderDetail[];
  refunds: any[];
  billingPerson: {
    name: string;
    firstName: string;
    lastName: string;
    street: string;
    city: string;
  };
  handlingFee: {
    value: number;
    valueWithoutTax: number;
    taxes: any[];
  };
  predictedPackage: any[];
  shipments: any[];
  shippingPerson?: any;
  shippingOption?: any;
  additionalInfo: any;
  contact?: any;
  paymentParams: any;
  hidden: boolean;
  taxesOnShipping: any[];
  acceptMarketing: boolean;
  disableAllCustomerNotifications: boolean;
  externalFulfillment: boolean;
  invoices: [
    {
      internalId: number;
      id: string;
      created: string;
      link: string;
      type: string;
    }
  ];
  pricesIncludeTax: boolean;
}

export interface ProductOrderDetail {
  id: number;
  productId: number;
  categoryId: number;
  price: number;
  priceWithoutTax: number;
  productPrice: number;
  sku: string;
  quantity: number;
  shortDescription: string;
  shortDescriptionTranslated: {
    es_419: string;
  };
  tax: number;
  shipping: number;
  quantityInStock: number;
  name: string;
  nameTranslated: {
    es_419: string;
  };
  isShippingRequired: false;
  trackQuantity: true;
  fixedShippingRateOnly: false;
  fixedShippingRate: number;
  digital: false;
  productAvailable: true;
  couponApplied: false;
  selectedOptions: [
    {
      name: string;
      nameTranslated: {
        es_419: string;
      };
      value: string;
      valueTranslated: {
        es_419: string;
      };
      valuesArray: [
        string
      ];
      valuesArrayTranslated: {
        es_419: [
          string
        ];
      };
      selections: [
        {
          selectionTitle: string;
          selectionModifier: number;
          selectionModifierType: string;
        }
      ];
      type: string;
    }
  ];
  discountsAllowed: boolean;
  taxable: boolean;
  giftCard: boolean;
  isCustomerSetPrice: boolean;
}

export enum ORDER_STATUS {
  AWAITING_PROCESSING = 'AWAITING_PROCESSING',
  PROCESSING = 'PROCESSING',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  WILL_NOT_DELIVER = 'WILL_NOT_DELIVER',
  RETURNED = 'RETURNED'
}

export enum PAYMENT_STATUS {
  AWAITING_PAYMENT = 'AWAITING_PAYMENT',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED'
}
export enum SHIPPING_METHODS {
  ENVIOCLICK = 'envioclick',
}
export enum SHIPPING_STATUS {
  PROGRESS = 'progress',
  UNKNOWN = 'unknown',
}

export interface IUpdateStatusOrderPayload {
  id?: string;
  paymentStatus: string;
  fulfillmentStatus: string;
}

export interface ICreateOrderPayload {
  total: number;
  email: string;
  paymentStatus: string;
  fulfillmentStatus: string;
  items: any[]
}

// ---------------- PRODUCTS: ---------------
export interface IProductApiModel {
  id: number;
  sku: string;
  quantity: number;
  unlimited: boolean;
  inStock: boolean;
  name: string;
  nameTranslated?: {
    es_419: string;
  };
  price: number;
  priceInProductList: number;
  defaultDisplayedPrice: number;
  defaultDisplayedPriceFormatted: string;
  tax?: {
    taxable: boolean;
    defaultLocationIncludedTaxRate?: number;
    enabledManualTaxes?: [];
    taxClassCode?: string;
  };
  compareToPrice: number;
  compareToPriceFormatted: string;
  compareToPriceDiscount: number;
  compareToPriceDiscountFormatted: string;
  compareToPriceDiscountPercent: number;
  compareToPriceDiscountPercentFormatted: string;
  isShippingRequired: boolean;
  url: string;
  created: string;
  updated: string;
  createTimestamp: number;
  updateTimestamp: number;
  productClassId: number;
  enabled: boolean;
  options?: any;
  warningLimit: number;
  fixedShippingRateOnly: boolean;
  fixedShippingRate: number;
  shipping: {
    type: string;
    methodMarkup: number;
    flatRate: number;
    disabledMethods?: any;
    enabledMethods?: any;
  };
  defaultCombinationId: number;
  description: string;
  descriptionTranslated: {
    es_419: string;
  };
  galleryImages: IProductImageGallery[];
  media?: {
    images?: IProductMediaImages[];
  };
  categoryIds?: number[];
  categories?: [
    {
      id: number;
      enabled: boolean;
    }
  ];
  defaultCategoryId?: number;
  seoTitle?: string;
  seoDescription?: string;
  attributes?: any;
  files?: any;
  relatedProducts?: {
    productIds?: any;
    relatedCategory?: {
      enabled: boolean;
      categoryId: number;
      productCount: number;
    };
  };
  combinations?: any;
  isSampleProduct: boolean;
  googleItemCondition: string;
  isGiftCard: boolean;
  discountsAllowed: boolean;
  nameYourPriceEnabled: boolean;
  productCondition: string;

}

export interface IProductImageGallery {
  id: number;
  url: string;
  thumbnail: string;
  originalImageUrl: string;
  imageUrl: string;
  hdThumbnailUrl: string;
  thumbnailUrl: string;
  smallThumbnailUrl: string;
  width: number;
  height: number;
  orderBy: number;
  borderInfo?: {
    dominatingColor?: any;
    homogeneity?: boolean;
  };
}

export interface IProductMediaImages {
  id: string;
  isMain: boolean;
  orderBy: number;
  image160pxUrl: string;
  image400pxUrl: string;
  image800pxUrl: string;
  image1500pxUrl: string;
  imageOriginalUrl: string;
}

export interface IUpsertProductPayload {
  id?: number;
  sku: string;
  quantity: number;
  name: string;
  nameTranslated?: {
    es: string;
  };
  price: number;
  compareToPrice?: number;
  isShippingRequired: boolean;
  categoryIds?: any[];
  weight?: number;
  enabled: boolean;
  description?: string;
  productClassId: number;
  created?: string;
  fixedShippingRateOnly: boolean;
  fixedShippingRate: number;
  options?: any;
  shipping?: {
    type: string;
    methodMarkup: number;
    flatRate: number;
    disabledMethods?: any[];
    enabledMethods?: any[];
  };
  galleryImages?: any[];
  unlimited: boolean;
  media?: {
    images?: IProductMediaImages[];
  };
}

// ---------------- VARIATIONS: -------------
export interface IVariationApiModel {
  id: number;
  name: string;
  options: IVariationOptionApiModel[];
  required: boolean;
  resource_uri: string;
  shop?: number;
  shop_id: number;
}

export interface IVariationOptionApiModel {
  id: number;
  name: string;
  order: number;
  price: string;
  variation_id: number;
}

export interface ICreateVariationPayload {
  name: string;
  required: boolean;
  options: Array<any[]>;
}
// ----------------- IMAGES: --------------------
export interface IImageApiModel {
  absolute_path: string;
  added: string;
  alt?: any;
  base_url?: string;
  checksum: string;
  filesize: number;
  height: number;
  id: number;
  image: string;
  is_global: boolean;
  last_increment: number;
  mimetype: string;
  modified: string;
  resource_uri: string;
  site_id: number;
  sizes: IImageSizesApiModel;
  type: string;
  user_id: number;
  versions: IImageVersionApiModel[];
  whitelabel_id: number;
  width: number;
}

export interface IImageVersionApiModel {
  absolute_path: string;
  filesize: number;
  height: number;
  id: number;
  image: string;
  image_id: number;
  is_global: boolean;
  mimetype: string;
  resource_uri: string;
  type: string;
  width: number;
}

export interface IImageSizesApiModel {
  big: string;
  large: string;
  medium: string;
  original: string;
  small: string;
  thumbnail: string;
}

// ---------------- ECWID: ---------------
export interface ILinkValidation {
  url: string;
  id: string;
  type: string;
  attributes: ILink;
}

export interface ILink {
 url: string;
  sso_url?: string;
}

export interface IToken {
  id: string;
  type: string;
}

export interface IProductOption {
  id: number;
  type: string;
  name: string;
  nameTranslated: {
    es_419: string;
  };
  defaultChoice?: number;
  required?: boolean;
  choices?: IChoice[];
}

export interface IChoice {
  typeChoice: string;
  text: string;
  textTranslated: {
    es?: string;
    es_419?: string;
  };
  priceModifier: number;
  priceModifierType: string;
}

export interface IEcwidImageUpload {
  file: string;
  id?: number;
}

export interface ICategoryImageUploadApiModel {
  orderBy: number;
  hdThumbnailUrl: string;
  thumbnailUrl: string;
  originalImageUrl: string;
  imageUrl: string;
  originalImage: any;
  thumbnail: any;
  borderInfo: any;
  name: string;
  nameTranslated: any;
  url: string;
  productCount: number;
  description: string;
  descriptionTranslated?: any;
  enabled: boolean;
  isSampleCategory: boolean;
  parentId?: number;
}

export interface IProfileApiModel {
  id: string;
  type: string;
  attributes: ILink;
}
